import {CartComparator} from './cart-comparator';
import {FactoryFn} from './factory-function';
import {
  ProductSpec as ClareProductSpec,
  SpecOption as ClareSpecOption,
} from '@sportngin/se-commerce-sdk/models/clare';

interface SpecificationInterface {
  ownerId: string;
  id: string;
  listOrder: number;
  name: string;
  required: boolean;
  options: SpecificationOption[];
}

interface SpecificationOptionInterface {
  id?: string;
  value?: string;
  listOrder?: number;
  isOpenText?: boolean;
  priceMarkup?: number;
  priceMarkupType?: PriceMarkupType;
}

const clareOptionToSpecificationOption: FactoryFn<
  ClareSpecOption,
  SpecificationOption
> = (source: ClareSpecOption) => {
  return new SpecificationOption({
    id: String(source.id),
    value: source.name,
    listOrder: source.display_order ?? 0,
  });
};

const clareSpecToSpecification: FactoryFn<ClareProductSpec, Specification> = (
  source: ClareProductSpec,
) => {
  return new Specification({
    id: String(source.id),
    ownerId: '', // Clare ProductSpec does not have an ownerId field
    listOrder: source.display_order ?? 0,
    name: String(source.name),
    required: true,
    options: source.spec_options
      ? source.spec_options.map(clareOptionToSpecificationOption)
      : [],
  });
};

class Specification
  implements SpecificationInterface, CartComparator<Specification>
{
  ownerId: string;
  id: string;
  listOrder: number;
  name: string;
  required: boolean;
  options: SpecificationOption[];

  constructor(src?: SpecificationInterface) {
    if (!src) return;

    this.id = src.id;
    this.name = src.name;
    this.ownerId = src.ownerId;
    this.listOrder = src.listOrder ?? 0;
    this.required = src.required;
    this.options = [...src.options];
  }

  get optionCount(): number {
    return this.options?.length ?? 0;
  }

  /**
   * Determines Specification equality based on the ID and options. Please note
   * that this method does NOT perform a deep equality but is meant to determine if
   * an item exists in the cart.
   * @param a the Specification this is being compared to.
   * @param b this
   */
  equals(a: Specification, b: Specification = this): boolean {
    let result = !!a && a.id === b.id && a.optionCount === b.optionCount;
    if (result && b.optionCount > 0) {
      for (const option of a.options) {
        result = b.options.findIndex(o => o.equals(option)) > -1;
        if (!result) break;
      }
    }
    return result;
  }
}

class SpecificationOption
  implements SpecificationOptionInterface, CartComparator<SpecificationOption>
{
  id?: string;
  value?: string;
  listOrder: number;
  // isOpenText: boolean;
  priceMarkup: number;
  priceMarkupType: PriceMarkupType;

  constructor(src?: SpecificationOptionInterface) {
    if (!src) return;

    this.id = src.id;
    this.value = src.value;
    this.listOrder = src.listOrder ?? 0;
    this.priceMarkup = src.priceMarkup ?? 0;
    this.priceMarkupType = src.priceMarkupType ?? PriceMarkupType.NO_MARKUP;
  }

  /**
   * Determines SpecificationOption equality based on the id, value and price markup.
   * @param a the SpecificationOption this is being compared to.
   * @param b this
   */
  equals(a: SpecificationOption, b: SpecificationOption = this): boolean {
    let result = !!a && a.id === b.id;
    if (result) {
      result =
        b.value === a.value &&
        b.priceMarkup === a.priceMarkup &&
        b.priceMarkupType === a.priceMarkupType;
    }
    return result;
  }
}

enum PriceMarkupType {
  NO_MARKUP = 'NoMarkup',
  AMOUNT_PER_QUANTITY = 'AmountPerQuantity',
  AMOUNT_TOTAL = 'AmountTotal',
  PERCENTAGE = 'Percentage',
}

interface SpecificationMap {
  [id: string]: Specification[];
}

export {
  Specification,
  SpecificationOption,
  SpecificationInterface,
  SpecificationOptionInterface,
  PriceMarkupType,
  SpecificationMap,
  clareSpecToSpecification,
  clareOptionToSpecificationOption,
};
